import { Location } from '@angular/common';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { IMPERSONATION_LANDING_PATH } from '../auth-storage.constants';
import {
  isImpersonationSession,
  readSessionToken,
  readStoredToken,
  storeSessionToken,
} from '../auth-storage.util';
import { isTokenUnexpired } from '../token-validity.util';
import { AUTH_TAB_SYNC_REQUEST_ID_BYTES } from './auth-tab-sync.constants';
import {
  AuthTabSyncMessage,
  AuthTabSyncMessageType,
  SessionReplyMessage,
} from './auth-tab-sync.messages';
import {
  AUTH_TAB_SYNC_REPLY_TIMEOUT,
  AUTH_TAB_SYNC_TRANSPORT_FACTORY,
  AuthTabSyncTransport,
} from './auth-tab-sync.transport';

const QUERY_STRING_SEPARATOR = '?';
const HEXADECIMAL_RADIX = 16;
const HEXADECIMAL_BYTE_WIDTH = 2;

interface PendingSessionRequest {
  requestId: string;
  settle(adopted: boolean): void;
}

function createRequestId(): string {
  const bytes = crypto.getRandomValues(
    new Uint8Array(AUTH_TAB_SYNC_REQUEST_ID_BYTES)
  );
  return Array.from(bytes, (byte) =>
    byte.toString(HEXADECIMAL_RADIX).padStart(HEXADECIMAL_BYTE_WIDTH, '0')
  ).join('');
}

/**
 * Partage de la session entre les onglets de l'application.
 *
 * La session « non mémorisée » (« se souvenir de moi » décoché) vit dans le
 * sessionStorage, propre à chaque onglet : un lien ouvert dans un nouvel
 * onglet démarrait donc déconnecté. Au démarrage, un onglet sans jeton demande
 * celui des onglets connectés et le range dans SON sessionStorage : la session
 * se ferme toujours avec le navigateur.
 *
 * Règles de sécurité :
 * - un onglet de prise d'identité ne répond jamais, ne demande jamais et
 *   n'adopte jamais de jeton ; il ignore aussi les déconnexions des autres ;
 * - seul le jeton de session non mémorisée est transmis, jamais s'il est
 *   expiré ; l'onglet demandeur le revérifie avant de l'adopter ;
 * - un jeton n'est adopté qu'en réponse à SA propre demande, en attente ;
 * - une déconnexion explicite est diffusée à tous les onglets.
 */
@Injectable({ providedIn: 'root' })
export class AuthTabSyncService {
  private readonly createTransport = inject(AUTH_TAB_SYNC_TRANSPORT_FACTORY);
  private readonly replyTimeoutMs = inject(AUTH_TAB_SYNC_REPLY_TIMEOUT);
  private readonly location = inject(Location);
  private readonly destroyRef = inject(DestroyRef);

  private readonly remoteLogoutSubject = new Subject<void>();
  /** Émis quand un autre onglet s'est déconnecté (jamais en prise d'identité). */
  readonly remoteLogout$: Observable<void> =
    this.remoteLogoutSubject.asObservable();

  private transport?: AuthTabSyncTransport;
  private pendingRequest?: PendingSessionRequest;

  /** Écoute les autres onglets (idempotent). */
  start(): void {
    this.ensureTransport();
  }

  /**
   * Au démarrage d'un onglet sans jeton : demande la session des onglets
   * connectés et attend au plus le délai de réponse. Résout `true` si un jeton
   * a été adopté, `false` sinon (aucune demande nécessaire, aucun onglet
   * connecté, ou réponse refusée).
   */
  requestSessionToken(): Promise<boolean> {
    const transport = this.ensureTransport();
    if (!this.needsSessionToken()) {
      return Promise.resolve(false);
    }
    return new Promise<boolean>((resolve) => {
      const requestId = createRequestId();
      const timer = setTimeout(() => settle(false), this.replyTimeoutMs);
      const settle = (adopted: boolean) => {
        clearTimeout(timer);
        this.pendingRequest = undefined;
        resolve(adopted);
      };
      this.pendingRequest = { requestId, settle };
      if (
        !this.post(transport, {
          type: AuthTabSyncMessageType.SessionRequest,
          requestId,
        })
      ) {
        settle(false);
      }
    });
  }

  /**
   * Diffuse une déconnexion explicite. L'appelant ne le fait jamais depuis un
   * onglet de prise d'identité, dont la fin ne concerne que lui.
   */
  broadcastLogout(): void {
    this.post(this.ensureTransport(), { type: AuthTabSyncMessageType.Logout });
  }

  /**
   * Un canal indisponible (stockage plein ou bloqué pour le repli) ne doit ni
   * bloquer le démarrage ni interrompre une déconnexion : l'onglet se comporte
   * alors comme avant le partage. Le message (qui peut porter un jeton) n'est
   * jamais journalisé.
   */
  private post(
    transport: AuthTabSyncTransport,
    message: AuthTabSyncMessage
  ): boolean {
    try {
      transport.post(message);
      return true;
    } catch (error: unknown) {
      console.warn(
        'Partage de session entre onglets indisponible',
        error instanceof Error ? error.name : error
      );
      return false;
    }
  }

  private ensureTransport(): AuthTabSyncTransport {
    if (!this.transport) {
      const transport = this.createTransport();
      transport.listen((message) => this.handleMessage(message));
      this.destroyRef.onDestroy(() => {
        this.pendingRequest?.settle(false);
        transport.close();
      });
      this.transport = transport;
    }
    return this.transport;
  }

  private handleMessage(message: AuthTabSyncMessage): void {
    switch (message.type) {
      case AuthTabSyncMessageType.SessionRequest:
        this.replyWithSessionToken(message.requestId);
        break;
      case AuthTabSyncMessageType.SessionReply:
        this.adoptSessionToken(message);
        break;
      case AuthTabSyncMessageType.Logout:
        if (!isImpersonationSession()) {
          this.remoteLogoutSubject.next();
        }
        break;
    }
  }

  private replyWithSessionToken(requestId: string): void {
    if (isImpersonationSession()) {
      return;
    }
    const token = readSessionToken();
    if (!isTokenUnexpired(token)) {
      return;
    }
    if (this.transport) {
      this.post(this.transport, {
        type: AuthTabSyncMessageType.SessionReply,
        requestId,
        token,
      });
    }
  }

  private adoptSessionToken({ requestId, token }: SessionReplyMessage): void {
    const pending = this.pendingRequest;
    if (!pending || pending.requestId !== requestId) {
      return;
    }
    if (!this.needsSessionToken() || !isTokenUnexpired(token)) {
      return;
    }
    storeSessionToken(token);
    pending.settle(true);
  }

  private needsSessionToken(): boolean {
    return (
      !isImpersonationSession() &&
      readStoredToken() === null &&
      !this.isImpersonationLanding()
    );
  }

  /** Onglet ouvert par le backoffice pour une prise d'identité (avant échange du code). */
  private isImpersonationLanding(): boolean {
    const [path] = this.location.path().split(QUERY_STRING_SEPARATOR);
    return path === `/${IMPERSONATION_LANDING_PATH}`;
  }
}
